import type {ReactNode} from 'react';
import React from 'react';
import LevelBadge from 'assets/images/adaptive/LevelBadge';

import {PROFICIENCY_SEGMENTS_BY_METHOD} from 'client/Reports/lib/constants/proficiencySegments';
import type {ProficiencyCountUnit} from 'client/Reports/lib/utils/proficiencyCountUnit';
import {getProficiencyNote} from 'client/Reports/lib/utils/proficiencyCountUnit';

export type Segment = {
  label: string;
  value: number;
  percentage: number;
  color: string;
};

const DEFAULT_INDEX = 4;

export const SORT_KEY_TO_INDEX: Record<string, number> = Object.fromEntries(
  Object.values(PROFICIENCY_SEGMENTS_BY_METHOD).flatMap(({segments}) =>
    segments.map((segment) => [segment.sortKey, segment.index])
  )
);

export const getSelectedIndex = (sortBy: string | null | undefined) => {
  // default shows Advanced (index 4)
  if (!sortBy) {
    return DEFAULT_INDEX;
  }
  return SORT_KEY_TO_INDEX[sortBy] ?? DEFAULT_INDEX;
};

/** The pre-`adaptive_diagnostics` note. Delete with the flag. */
export const getLegacyPercentageLabel = (
  segments: Segment[],
  selectedIndex: number,
  allNotStarted: boolean
) => {
  if (allNotStarted) {
    return 'Not started: 100%';
  }
  const seg = segments[selectedIndex];
  return `${seg.label}: ${seg.percentage}%`;
};

/**
 * The note under the bar. It only appears once the scope pins one side of the
 * (student, skill) pair, since a bare percentage of pair counts says nothing on its own.
 */
export const getPercentageLabel = (
  segments: Segment[],
  selectedIndex: number,
  countUnit: ProficiencyCountUnit,
  total: number,
  allNotStarted: boolean
) => {
  // Highlighting the sorted bucket would read "0 of 25 students at Advanced" for a row nobody has
  // touched, so an untouched row names the bucket that actually holds everything.
  const seg = allNotStarted ? segments[0] : segments[selectedIndex];
  return getProficiencyNote({
    unit: countUnit,
    bucketLabel: seg.label,
    bucketCount: seg.value,
    total
  });
};

export const getIconForIndex = (index: number): ReactNode | null => {
  switch (index) {
    case 2:
      return <LevelBadge width={16} height={16} fidelity='low' levelNum={1} />;
    case 3:
      return <LevelBadge width={16} height={16} fidelity='low' levelNum={2} />;
    case 4:
      return <LevelBadge width={16} height={16} fidelity='low' levelNum={3} />;
    default:
      return null; // Not started / Unranked
  }
};
